import { useEffect, useState } from "react";
import { Organization } from "../../models/interfaces/organization";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import OrganizationService from "../../utilities/services/organization-service";
import { Loader } from "../loader/loader";
import PortalProgressBar from "../portal-progress-bar/portal-progress-bar";
import { useTranslation } from "react-i18next";
import { createLogger } from "../../utilities/logger";
import { SummaryTile } from "../summary-tile/summary-tile";

const COMPONENT_CLASS = "c-billing";
const log = createLogger("SubOrganizationBilling");

/**
 * Read-only billing view for sub-organizations. Sub-organizations have no
 * subscription of their own; billing shows the slices allocated by the head
 * (parent) organization so admins with access can monitor their limits.
 */
const SubOrganizationBilling: React.FC = () => {
    const { state } = useAuthState();
    const { t } = useTranslation();
    const [organization, setOrganization] = useState<Organization>();
    const [parentOrganization, setParentOrganization] = useState<Organization>();
    const [isLoading, setIsLoading] = useState(true);

    const orgId = state.organization?.id;
    const parentOrgId = state.organization?.parentOrganizationId;

    useEffect(() => {
        if (orgId == null) {
            return;
        }

        setIsLoading(true);

        const unsubscribe = OrganizationService.getSnapshot(orgId, (org: Organization) => {
            setOrganization(org);
            setIsLoading(false);
        });

        if (parentOrgId) {
            OrganizationService.get(parentOrgId)
                .then((parent) => {
                    if (!parent) {
                        log.warn("Parent organization not found for sub-org billing", {
                            organizationId: orgId,
                            parentOrganizationId: parentOrgId,
                        });
                    }
                    setParentOrganization(parent ?? undefined);
                })
                .catch((e) => {
                    log.error("Failed to load parent organization for sub-org billing", {
                        exception: e,
                        organizationId: orgId,
                        parentOrganizationId: parentOrgId,
                    });
                });
        } else {
            setParentOrganization(undefined);
        }

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [orgId, parentOrgId]);

    const userCount = Number(organization?.userCount ?? 0);
    const userLimit = Number(organization?.userLimit ?? 0);
    const licenseCount = Number(organization?.mySymmioLicenseCount ?? 0);
    const licenseLimit = Number(organization?.mySymmioLicense ?? 0);

    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isLoading} />
            <div className={`${COMPONENT_CLASS}__status`}>
                <div className={`${COMPONENT_CLASS}__status__container`}>
                    <SummaryTile
                        className={`${COMPONENT_CLASS}__status__tile`}>
                        <div className={`${COMPONENT_CLASS}__status__tile-header`}>
                            <h1 className={`${COMPONENT_CLASS}__status__tile-title`}>{t('billing.subOrganization')}</h1>
                            {parentOrganization?.name &&
                                <p className={`label-small ${COMPONENT_CLASS}__status__tile-subtitle`}>
                                    {t('billing.parentOrganization', { name: parentOrganization.name })}
                                </p>
                            }
                        </div>
                        <div className={`${COMPONENT_CLASS}__status__tile-main -auto`}>
                            {userLimit > 0 &&
                                <PortalProgressBar
                                    total={userLimit}
                                    currentProgress={userCount}
                                    headerText={t('licenseUserRemoval.usedUserProgress', { userCount, userLimit })}
                                />
                            }
                            {licenseLimit > 0 &&
                                <PortalProgressBar
                                    total={licenseLimit}
                                    currentProgress={licenseCount}
                                    headerText={t('licenseUserRemoval.usedLicenseProgress', { mySymmioLicenseCount: licenseCount, mySymmioLicenseLimit: licenseLimit })}
                                />
                            }
                        </div>
                    </SummaryTile>
                    <SummaryTile
                        className={`${COMPONENT_CLASS}__status__tile`}>
                        <div className={`${COMPONENT_CLASS}__status__tile-header`}>
                            <h1 className={`${COMPONENT_CLASS}__status__tile-title`}>{t('billing.paymentType')}</h1>
                        </div>
                        <div className={`${COMPONENT_CLASS}__status__tile-main -auto`}>
                            {parentOrganization?.name &&
                                <p className={`${COMPONENT_CLASS}__custom-payment-message`}>
                                    {t('billing.managedByParent', { name: parentOrganization.name })}
                                </p>
                            }
                        </div>
                    </SummaryTile>
                </div>
            </div>
        </div>
    );
};

export default SubOrganizationBilling;